import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import { Link } from 'lego-on-react';
import React from 'react';
import Dropzone from 'react-dropzone';

import { ButtonDecorator } from '../../components/ButtonDecorator/ButtonDecorator';
import { Shape2, Shape2Icon } from '../../components/Shape2/Shape2';
import { Bem } from '../../service/Bem/Bem';

import './Html5Dropzone.css';
import * as keyset from './Html5Dropzone.i18n';

const BLOCK = 'html5-dropzone';
const i18n = i18nFactory(keyset);
const i18nRaw = i18nRawFactory(keyset);

interface Html5DropzoneProps {
    hasCreatives: boolean;
    filesSelected: (files: File[]) => void;
    helpUrl: string;
}

export class Html5Dropzone extends React.PureComponent<Html5DropzoneProps, { dragging: boolean }> {
    state = { dragging: false };

    private dropzone: React.RefObject<Dropzone> & React.RefObject<HTMLDivElement> = React.createRef();
    private draggingTimeout: number;

    componentDidMount(): void {
        document.addEventListener('dragenter', this.onDragEnter);
        document.addEventListener('dragover', this.onDragEnter);
        document.addEventListener('dragleave', this.onDragLeave);
    }

    componentWillUnmount(): void {
        document.removeEventListener('dragenter', this.onDragEnter);
        document.removeEventListener('dragover', this.onDragEnter);
        document.removeEventListener('dragleave', this.onDragLeave);
    }

    render() {
        return (
            <div
                className={Bem.cls(BLOCK, 'uploads', {
                    dragging: this.state.dragging ? 'active' : '',
                    files: this.props.hasCreatives ? 'yes' : ''
                })}
            >
                <Dropzone
                    ref={this.dropzone}
                    className={Bem.cls(BLOCK, 'dropzone', {
                        dragging: this.state.dragging ? 'active' : ''
                    })}
                    style={{}}
                    onDrop={this.onFileDrop}
                    disabled={false}
                >
                    <div />
                </Dropzone>
                <div className={Bem.cls(BLOCK, 'text-wrapper')}>
                    <Shape2 cls={Bem.cls(BLOCK, 'upload-icon')} width={40} height={40} icon={Shape2Icon.upload} />
                    <div className={Bem.cls(BLOCK, 'dropzone-title')}>{i18n('drop-files-here')}</div>
                    <div className={Bem.cls(BLOCK, 'dropzone-subtitle')}>
                        {i18nRaw('press-upload', {
                            requirementsLink: (
                                <Link key={'link'} target={'blank'} theme={'normal'} url={this.props.helpUrl}>
                                    {i18n('requirements')}
                                </Link>
                            )
                        })}
                    </div>
                    <ButtonDecorator size={'m'} theme={'normal'} text={i18n('upload')} onClick={this.onUploadClick}>
                        <Shape2 cls={Bem.cls(BLOCK, 'btn-icon')} width={16} height={16} icon={Shape2Icon.upload} />
                    </ButtonDecorator>
                </div>
            </div>
        );
    }

    upload = () => {
        if (this.dropzone.current) {
            this.dropzone.current.open();
        }
    };

    private onUploadClick = () => {
        this.upload();
    };

    private onDragEnter = (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.dataTransfer && this.isFilesDragging(e.dataTransfer)) {
            this.setState({ dragging: true });
            this.setDraggingTimeout();
        }
    };

    private onDragLeave = (e: DragEvent) => {
        e.preventDefault();

        if (e.target === document) {
            this.setState({ dragging: false });
        }
    };

    private setDraggingTimeout(): void {
        if (this.draggingTimeout) {
            clearTimeout(this.draggingTimeout);
        }
        this.draggingTimeout = window.setTimeout(() => {
            this.setState({ dragging: false });
        }, 200);
    }

    private isFilesDragging(data: DataTransfer): boolean {
        return data.types.includes('Files') || data.types.includes('application/x-moz-file');
    }

    private onFileDrop = (accepted: File[], rejected: File[]) => {
        this.props.filesSelected(accepted.concat(rejected));
    };
}
